// ============================================================
// Reminders / Alarm clock store (localStorage-backed)
//
// Powers the alarm-clock widget in the service top bar
// (src/components/layout/ServiceTopBar.tsx). Pure data layer —
// no React. The widget polls these helpers once a second and
// fires any reminder whose time has arrived.
// ============================================================

const STORAGE_KEY = 'akt_reminders';

export type RepeatMode = 'once' | 'daily' | 'weekdays' | 'weekends';

export interface Reminder {
  id: number;
  label: string;
  /** 24h "HH:MM" */
  time: string;
  repeat: RepeatMode;
  enabled: boolean;
  emoji: string;
  color: string;
  /** "YYYY-MM-DD" of the day it last rang — stops repeats double-firing */
  lastFired?: string;
  /** epoch ms — when snoozed, ring again at/after this moment */
  snoozeUntil?: number;
  createdAt: string;
}

export const REMINDER_COLORS = [
  '#E8862A', // brand orange
  '#1565c0', // blue
  '#2e7d32', // green
  '#c62828', // red
  '#6a1b9a', // purple
  '#00838f', // teal
];

export const REMINDER_EMOJIS = ['⏰', '🔔', '☕', '📞', '💊', '🏃', '🍽️', '📈', '🧘', '💧', '📝', '🎯'];

export const REPEAT_LABELS: Record<RepeatMode, string> = {
  once: 'Once',
  daily: 'Every day',
  weekdays: 'Weekdays',
  weekends: 'Weekends',
};

function readAll(): Reminder[] {
  try {
    const raw = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
    return Array.isArray(raw) ? raw : [];
  } catch {
    return [];
  }
}

function writeAll(list: Reminder[]): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
  // Let other tabs / components react
  window.dispatchEvent(new Event('akt_reminders_changed'));
}

export function getReminders(): Reminder[] {
  return readAll().sort((a, b) => a.time.localeCompare(b.time));
}

export function addReminder(
  data: Pick<Reminder, 'label' | 'time' | 'repeat' | 'emoji' | 'color'>
): Reminder {
  const list = readAll();
  const reminder: Reminder = {
    id: list.length ? Math.max(...list.map(r => r.id)) + 1 : 1,
    label: data.label.trim() || 'Reminder',
    time: data.time,
    repeat: data.repeat,
    emoji: data.emoji,
    color: data.color,
    enabled: true,
    createdAt: new Date().toISOString(),
  };
  list.push(reminder);
  writeAll(list);
  return reminder;
}

export function updateReminder(id: number, patch: Partial<Reminder>): void {
  const list = readAll();
  const idx = list.findIndex(r => r.id === id);
  if (idx >= 0) {
    list[idx] = { ...list[idx], ...patch };
    writeAll(list);
  }
}

export function deleteReminder(id: number): void {
  writeAll(readAll().filter(r => r.id !== id));
}

export function toggleReminder(id: number): void {
  const list = readAll();
  const idx = list.findIndex(r => r.id === id);
  if (idx >= 0) {
    list[idx].enabled = !list[idx].enabled;
    // re-enabling clears a stale "fired today" so it can ring again later today
    if (list[idx].enabled) delete list[idx].lastFired;
    writeAll(list);
  }
}

export function snoozeReminder(id: number, minutes: number): void {
  updateReminder(id, { snoozeUntil: Date.now() + minutes * 60_000 });
}

// ---- scheduling helpers ----

function dayKey(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Does this repeat mode include the given weekday? (0 = Sun … 6 = Sat) */
function repeatMatchesDay(repeat: RepeatMode, weekday: number): boolean {
  switch (repeat) {
    case 'daily':
    case 'once':
      return true;
    case 'weekdays':
      return weekday >= 1 && weekday <= 5;
    case 'weekends':
      return weekday === 0 || weekday === 6;
  }
}

/**
 * Return reminders that should ring *right now* and mark them as fired so they
 * don't re-ring. "once" reminders are disabled after firing; snoozed reminders
 * ring when their snooze elapses regardless of clock time.
 */
export function collectDueReminders(now: Date = new Date()): Reminder[] {
  const list = readAll();
  const today = dayKey(now);
  const hhmm = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
  const nowMs = now.getTime();
  const due: Reminder[] = [];
  let changed = false;

  for (const r of list) {
    if (!r.enabled) continue;

    // Snooze takes priority — fire once the snooze window closes.
    if (r.snoozeUntil && nowMs >= r.snoozeUntil) {
      due.push(r);
      delete r.snoozeUntil;
      r.lastFired = today;
      if (r.repeat === 'once') r.enabled = false;
      changed = true;
      continue;
    }
    if (r.snoozeUntil) continue; // still snoozing — don't also clock-fire

    const isTime = r.time === hhmm && repeatMatchesDay(r.repeat, now.getDay());
    const alreadyFiredToday = r.lastFired === today;
    if (isTime && !alreadyFiredToday) {
      due.push(r);
      r.lastFired = today;
      if (r.repeat === 'once') r.enabled = false;
      changed = true;
    }
  }

  if (changed) writeAll(list);
  return due;
}

/**
 * The next reminder that will ring, with a human "in 2h 5m" string —
 * used for the widget's at-a-glance subtitle.
 */
export function getNextReminder(now: Date = new Date()): { reminder: Reminder; inMs: number } | null {
  const list = readAll().filter(r => r.enabled);
  let best: { reminder: Reminder; inMs: number } | null = null;

  for (const r of list) {
    if (r.snoozeUntil) {
      const inMs = r.snoozeUntil - now.getTime();
      if (inMs >= 0 && (!best || inMs < best.inMs)) best = { reminder: r, inMs };
      continue;
    }
    const [h, m] = r.time.split(':').map(Number);
    // search the next 8 days for the soonest matching weekday
    for (let add = 0; add < 8; add++) {
      const cand = new Date(now);
      cand.setDate(now.getDate() + add);
      cand.setHours(h, m, 0, 0);
      if (cand.getTime() <= now.getTime()) continue;
      if (!repeatMatchesDay(r.repeat, cand.getDay())) continue;
      const inMs = cand.getTime() - now.getTime();
      if (!best || inMs < best.inMs) best = { reminder: r, inMs };
      break;
    }
  }
  return best;
}

export function formatCountdown(ms: number): string {
  const totalMin = Math.round(ms / 60_000);
  if (totalMin < 1) return 'in <1 min';
  const days = Math.floor(totalMin / 1440);
  const hours = Math.floor((totalMin % 1440) / 60);
  const mins = totalMin % 60;
  const parts: string[] = [];
  if (days) parts.push(`${days}d`);
  if (hours) parts.push(`${hours}h`);
  if (mins && !days) parts.push(`${mins}m`);
  return `in ${parts.join(' ')}`;
}
